import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import * as _ from "lodash";
import { AppSessionService } from "../app-session/app-session.service";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import {
  setAdvancedFilterQuery,
  setSimpleFilterQuery,
} from "../../utils/tableUtils";
const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class PropertiesService {
  private BASE_URL = this.appSession.baseUrl + "/api/v1/properties";

  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}

  getPropertiesUsage(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/properties"}/usages`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getProperties(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/search?"
      ),
      { headers }
    );
  }

  getPropertyDataType(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/properties"}/dataTypes`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getProperty(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/properties"}/${code}`;
    return this.http.get(url, { headers });
  }

  getPropertiesByUsage(
    pageReq: PaginationRequest,
    usageCode: string
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string)?.length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${
      this.appSession.baseUrl + "/api/v1/properties"
    }?usageCode=${usageCode}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false,
      };
    }

    const pagination =
      "?offset=" +
      pageReq.offset +
      (pageReq.limit ? "&limit=" + pageReq.limit : "") +
      "&real_size=" +
      pageReq.realSize +
      "&usageCode=" +
      (usageCode.length > 0 ? usageCode : undefined);

    // url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  createProperty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.property);
    const url = `${this.appSession.baseUrl + "/api/v1/properties"}`;
    return this.http.post(url, data, { headers });
  }

  updateProperty(propertyData): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    let tempPropertyData = {};
    Object.assign(tempPropertyData, propertyData);
    if ("type" in tempPropertyData) {
      tempPropertyData = {
        dataTypeCode: propertyData.data.dataTypeCode.code,
        code: propertyData.data.code,
        createdOn: propertyData.data.createdOn,
        description: propertyData.data.description,
        detailedDescription: propertyData.data.detailedDescription,
        enabled: propertyData.data.enabled,
        mandatory: propertyData.data.mandatory,
        updatedOn: propertyData.data.updatedOn,
        usages: propertyData.data.usages,
      };
    } else {
      tempPropertyData = _.cloneDeep(propertyData);
    }
    const data = JSON.stringify(tempPropertyData);
    const url = `${this.appSession.baseUrl + "/api/v1/properties"}/${
      (tempPropertyData as any)?.code
    }`;
    return this.http.put(url, data, { headers });
  }

  deleteProperty(data): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    // const data = JSON.stringify(property);
    const url =
      `${this.appSession.baseUrl + "/api/v1/properties"}/` +
      encodeURIComponent(data.data.code);
    return this.http.delete(url, { headers });
  }

  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    let baseQueryString = `${
      this.appSession.baseUrl + "/api/v1/properties"
    }${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = this._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }

  private _buildScrollExpression(pagingArgs): string {
    const skip = pagingArgs.skip;
    const top = pagingArgs.top;
    const realSize = pagingArgs.realSize;

    if (top && top !== null) {
      return `real_size=${!!realSize}&offset=${skip}&limit=${top}`;
    } else {
      return `real_size=${!!realSize}&offset=${skip}`;
    }
  }
}
